import { MimeType } from 'vet-bones/constants';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  CreationType,
  ModelInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Database } from 'src/store/state/databases';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { DEFAULT_MODEL } from 'src/templates/queries/iris';
import { exportModel } from 'src/utils/export/model';
import { stringifyProjectMetadata } from 'src/utils/stringify/projectMetadata';

export const publishModel = async ({
  database,
  project,
  modelInfo,
  publishedDate,
}: {
  database: Database;
  project: Project;
  modelInfo: ModelInfo;
  publishedDate: number;
}): Promise<PublishProjectResponse> => {
  const databaseId = database.id;
  const isReplace = modelInfo.creationType === CreationType.REPLACE;
  const modelCopy = isReplace
    ? copy.notifications.publishProject.model.replace
    : copy.notifications.publishProject.model.create;

  // Begin a transaction
  const transactionResponse =
    await databasesStardogRequestDispatchers.beginDatabaseTransaction(
      databaseId
    );

  const transactionId = transactionResponse.payload.response?.body;

  if (
    transactionResponse.type ===
      DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
    !transactionId
  ) {
    return {
      success: false,
      action: transactionResponse,
      actionDescription: copy.notifications.database.beginTransaction,
      message: modelCopy.failure,
    };
  }

  // Prep to rollback if we need to
  const rollback = async () => {
    await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
      databaseId,
      transactionId
    );
  };

  const namespaces = database.namespaces || {};
  const namedGraph = modelInfo.namedGraphIri;

  // Clear the existing model graph
  if (isReplace) {
    const clearGraphResponse =
      await databasesStardogRequestDispatchers.clearDatabaseNamedGraph({
        databaseId,
        transactionId,
        namedGraph,
      });

    if (
      clearGraphResponse.type ===
      DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_FAILURE
    ) {
      // Stardog doesn't actually start a transaction until an action
      // has succeeded, so no need to rollback the first one if it fails
      return {
        success: false,
        action: clearGraphResponse,
        actionDescription: copy.notifications.database.clearNamedGraph,
        message: modelCopy.failure,
      };
    }
  }

  // Publish the model
  const { contents, mimeType } = exportModel(
    project.name,
    (project.diagram as DiagramSnapshot).model,
    project.propertyType,
    namespaces
  );
  const addDataResponse =
    await databasesStardogRequestDispatchers.addDataToDatabase({
      databaseId,
      transactionId,
      contents,
      mimeType,
      namedGraph,
    });

  if (
    addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
  ) {
    if (isReplace) {
      await rollback();
    }
    return {
      success: false,
      action: addDataResponse,
      actionDescription: copy.notifications.database.addData,
      message: modelCopy.failure,
    };
  }

  // Publish the model metadata
  const metadata = stringifyProjectMetadata(
    namedGraph,
    project.name,
    publishedDate,
    namespaces
  );
  const addMetadataResponse =
    await databasesStardogRequestDispatchers.addDataToDatabase({
      databaseId,
      transactionId,
      contents: metadata,
      mimeType: MimeType.TRIG,
    });
  if (
    addMetadataResponse.type ===
    DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
  ) {
    await rollback();
    return {
      success: false,
      action: addMetadataResponse,
      actionDescription: copy.notifications.database.addData,
      message: modelCopy.failure,
    };
  }

  // Update database reasoning schemas
  const existingSchemas = new Set(database.models);
  existingSchemas.delete(DEFAULT_MODEL);
  if (isReplace) {
    existingSchemas.delete(modelInfo.modelName);
  }

  const reasoningSchemas = [`${modelInfo.modelName}=${namedGraph}`];
  existingSchemas.forEach((model) => {
    database.namedGraphsByModel?.[model].forEach((namedGraph) => {
      reasoningSchemas.push(`${model}=${namedGraph}`);
    });
  });
  const setDatabaseDetailsResponse =
    await databasesStardogRequestDispatchers.updateDatabaseDetails(databaseId, {
      'reasoning.schemas': reasoningSchemas,
    });
  if (
    setDatabaseDetailsResponse.type ===
    DatabasesActionType.UPDATE_DATABASE_DETAILS_FAILURE
  ) {
    await rollback();
    return {
      success: false,
      action: setDatabaseDetailsResponse,
      actionDescription: copy.notifications.database.updateDatabaseDetails,
      message: modelCopy.failure,
    };
  }

  // Commit the transaction
  const commitResponse =
    await databasesStardogRequestDispatchers.commitDatabaseTransaction(
      databaseId,
      transactionId
    );

  if (
    commitResponse.type ===
    DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
  ) {
    await rollback();
    return {
      success: false,
      action: commitResponse,
      actionDescription: copy.notifications.database.commitTransaction,
      message: modelCopy.failure,
    };
  }

  return {
    success: true,
    action: commitResponse,
    message: modelCopy.success(modelInfo.modelName),
  };
};
